import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Spinner } from '@components/ui';
import { useSuggestTask, useEstimateTask } from '@hooks';

export default function AIAssistant({ title, onApplySuggestions }) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState(null);

  const suggestTask = useSuggestTask();
  const estimateTask = useEstimateTask();

  const handleGetSuggestions = async () => {
    if (!title || title.length < 3) return;

    setShowSuggestions(true);
    try {
      const result = await suggestTask.mutateAsync({ title });
      setSuggestions(result.data);
    } catch (error) {
      console.error('Error getting suggestions:', error);
    }
  };

  const handleApply = (field, value) => {
    onApplySuggestions({ [field]: value });
  };

  const handleApplyAll = () => {
    if (!suggestions) return;
    onApplySuggestions({
      type: suggestions.type,
      priority: suggestions.priority,
      points: suggestions.points,
      description: suggestions.description,
    });
    setShowSuggestions(false);
  };

  const priorityLabels = {
    CRITICAL: 'Crítica',
    HIGH: 'Alta',
    MEDIUM: 'Media',
    LOW: 'Baja',
  };

  const typeLabels = {
    FEATURE: 'Feature',
    BUG: 'Bug',
    IMPROVEMENT: 'Mejora',
    TASK: 'Tarea',
  };

  if (!showSuggestions) {
    return (
      <button
        type="button"
        onClick={handleGetSuggestions}
        disabled={!title || title.length < 3}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '8px 14px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          border: 'none',
          borderRadius: '10px',
          fontSize: '13px',
          fontWeight: '600',
          cursor: title && title.length >= 3 ? 'pointer' : 'not-allowed',
          opacity: title && title.length >= 3 ? 1 : 0.5,
          transition: 'opacity 0.2s',
        }}
      >
        <span>✨</span>
        Mejorar con IA
      </button>
    );
  }

  return (
    <div
      style={{
        backgroundColor: '#f8fafc',
        border: '1px solid #e2e8f0',
        borderRadius: '12px',
        padding: '16px',
        marginTop: '12px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '20px' }}>✨</span>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
            Sugerencias de IA
          </h4>
        </div>
        <button
          onClick={() => setShowSuggestions(false)}
          style={{
            background: 'none',
            border: 'none',
            color: '#64748b',
            fontSize: '18px',
            cursor: 'pointer',
            padding: '4px',
          }}
        >
          ×
        </button>
      </div>

      {suggestTask.isPending ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <Spinner size="sm" />
          <span style={{ marginLeft: '12px', color: '#64748b', fontSize: '14px' }}>
            Analizando tarea...
          </span>
        </div>
      ) : suggestions ? (
        <div>
          {/* Suggestions Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            {/* Type */}
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '12px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: '#64748b' }}>Tipo</span>
                <button
                  onClick={() => handleApply('type', suggestions.type)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#667eea',
                    fontSize: '11px',
                    cursor: 'pointer',
                    fontWeight: '500',
                  }}
                >
                  Aplicar
                </button>
              </div>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                {typeLabels[suggestions.type]}
              </span>
            </div>

            {/* Priority */}
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '12px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: '#64748b' }}>Prioridad</span>
                <button
                  onClick={() => handleApply('priority', suggestions.priority)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#667eea',
                    fontSize: '11px',
                    cursor: 'pointer',
                    fontWeight: '500',
                  }}
                >
                  Aplicar
                </button>
              </div>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                {priorityLabels[suggestions.priority]}
              </span>
            </div>

            {/* Points */}
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '12px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: '#64748b' }}>Story Points</span>
                <button
                  onClick={() => handleApply('points', suggestions.points)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#667eea',
                    fontSize: '11px',
                    cursor: 'pointer',
                    fontWeight: '500',
                  }}
                >
                  Aplicar
                </button>
              </div>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                {suggestions.points} pts
              </span>
            </div>

            {/* Hours estimate */}
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '12px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: '#64748b' }}>Horas estimadas</span>
                <button
                  onClick={() => handleApply('hours', suggestions.points * 2)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#667eea',
                    fontSize: '11px',
                    cursor: 'pointer',
                    fontWeight: '500',
                  }}
                >
                  Aplicar
                </button>
              </div>
              <span style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b' }}>
                ~{suggestions.points * 2}h
              </span>
            </div>
          </div>

          {/* Description Preview */}
          {suggestions.description && (
            <div
              style={{
                backgroundColor: 'white',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                padding: '12px',
                marginBottom: '16px',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <span style={{ fontSize: '12px', color: '#64748b' }}>Plantilla de descripción</span>
                <button
                  onClick={() => handleApply('description', suggestions.description)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#667eea',
                    fontSize: '11px',
                    cursor: 'pointer',
                    fontWeight: '500',
                  }}
                >
                  Aplicar
                </button>
              </div>
              <pre
                style={{
                  fontSize: '12px',
                  color: '#475569',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace',
                  backgroundColor: '#f8fafc',
                  padding: '8px',
                  borderRadius: '6px',
                  maxHeight: '100px',
                  overflow: 'auto',
                  margin: 0,
                }}
              >
                {suggestions.description.substring(0, 200)}...
              </pre>
            </div>
          )}

          {/* Apply All Button */}
          <button
            onClick={handleApplyAll}
            style={{
              width: '100%',
              padding: '10px',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            Aplicar todas las sugerencias
          </button>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
          No se pudieron generar sugerencias
        </div>
      )}
    </div>
  );
}

AIAssistant.propTypes = {
  title: PropTypes.string,
  onApplySuggestions: PropTypes.func.isRequired,
};
